import * as React from 'react'
import { useEffect, useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { AppService } from '@halo/services/app.service'
import { Form, Input, Select } from 'antd'
import { PageAnalysisConfigsService } from '@halo/services/page-analysis-configs.service'
import { simpleNotification } from '@halo/_utils/notifications'
import { IHydratedPageAnalysisConfig } from '@halo/interfaces/hydrated-page-analysis-config'
import { DomainService } from '@halo/services/domain.service'
import { DomainDto } from '@halo/dtos/domain.dto'
import { Drawer } from '@pushly/aqe/lib/components'
import { useForm } from 'antd/es/form/Form'
import { deepMerge } from '@halo/_utils/object'
import { IPageAnalysisConfig } from '@pushly/aqe/lib/interfaces'

export interface IEditPageAnalysisConfigDrawerProps {
    pageAnalysisConfig: Partial<IHydratedPageAnalysisConfig>
    onUpdate: (pageAnalysisConfig: IHydratedPageAnalysisConfig) => void
    onClose: () => void
}

function EditPageAnalysisConfigDrawer(props: IEditPageAnalysisConfigDrawerProps) {
    const appService = Container.get(AppService)
    const [isSaving, setIsSaving] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [domains, setDomains] = useState<DomainDto[]>()
    const [pageAnalysisConfigs, setPageAnalysisConfigs] = useState<IPageAnalysisConfig[]>()

    const [form] = useForm()

    const pageAnalysisConfigService: PageAnalysisConfigsService = Container.get(PageAnalysisConfigsService)
    const domainsService: DomainService = Container.get(DomainService)

    useEffect(() => {
        const setDependencies = async () => {
            setIsLoading(true)

            form.setFieldsValue(props.pageAnalysisConfig)

            const res = await Promise.all([fetchDomains(), fetchPageAnalysisConfigs()])

            const [d, pacs] = res

            setDomains(d)
            setPageAnalysisConfigs(pacs)
            setIsLoading(false)
        }

        setDependencies()
    }, [props.pageAnalysisConfig])

    async function fetchDomains() {
        const domainFields = ['id', 'name', 'display_name']

        const res = await domainsService.fetchAll({
            query: {
                pagination: false,
                fields: domainFields.join(','),
            },
        })

        return res.data
    }

    async function fetchPageAnalysisConfigs() {
        const res = await pageAnalysisConfigService.fetchAll({
            query: { pagination: 0 },
        })
        return res.data
    }

    async function save() {
        setIsSaving(true)
        const saveDto = deepMerge(props.pageAnalysisConfig, form.getFieldsValue())

        try {
            let successMessage = ''
            let pacId = props.pageAnalysisConfig.id

            if (props.pageAnalysisConfig.id) {
                await pageAnalysisConfigService.patch(props.pageAnalysisConfig.id, saveDto)
                successMessage = 'Page Analysis Config Updated'
            } else {
                const nres = await pageAnalysisConfigService.create(saveDto)
                pacId = nres.data.id
                successMessage = 'Page Analysis Config Created'
            }

            // Fetch a new hydrated pac to get all extended pac fields
            const hres = await pageAnalysisConfigService.fetchById(pacId, {
                query: {
                    hydrated: true,
                },
            })
            simpleNotification('success', successMessage)
            props.onUpdate(hres.data as IHydratedPageAnalysisConfig)
        } catch (e) {
            simpleNotification('error', e.toString())
        }
        setIsSaving(false)
    }

    function render() {
        return (
            <Drawer
                width="60%"
                getContainer={appService.getAppContainer}
                className="edit-category-drawer"
                title={
                    props.pageAnalysisConfig && props.pageAnalysisConfig.id
                        ? 'Edit Page Analysis Config'
                        : 'Add Page Analysis Config'
                }
                placement="right"
                closable={!isSaving}
                submitText={
                    props.pageAnalysisConfig && props.pageAnalysisConfig.id
                        ? 'Update Page Analysis Config'
                        : 'Add Page Analysis Config'
                }
                disableSubmit={isSaving}
                onSubmit={async () => {
                    form.submit()
                }}
                onClose={props.onClose}
                visible={!!props.pageAnalysisConfig}
            >
                {props.pageAnalysisConfig && (
                    <Form
                        form={form}
                        layout="horizontal"
                        onFinish={() => {
                            save()
                        }}
                    >
                        <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Name is required' }]}>
                            <Input autoComplete="off" />
                        </Form.Item>

                        {!props.pageAnalysisConfig.is_default && (
                            <Form.Item label="Extends" name="parent_id">
                                <Select
                                    allowClear={true}
                                    filterOption={(input, option) => {
                                        return (
                                            option.props.children
                                                .toString()
                                                .toLowerCase()
                                                .indexOf(input.toLowerCase()) >= 0
                                        )
                                    }}
                                >
                                    {pageAnalysisConfigs?.map((d) => {
                                        if (d.id !== props.pageAnalysisConfig.id) {
                                            return (
                                                <Select.Option key={d.id} value={d.id}>
                                                    {d.name}
                                                </Select.Option>
                                            )
                                        }
                                    })}
                                </Select>
                            </Form.Item>
                        )}

                        <Form.Item label="Associated Domains" name="associated_domains">
                            <p style={{ lineHeight: 'normal' }}>
                                Domains can only have one explicit page analysis config association at a time. Removing
                                a domain page analysis config association here will cause the domain to fallback to the
                                Default page analysis config. Adding a domain page analysis config association here will
                                overwrite any preexisting explicit association that domain may have.
                            </p>

                            <Select
                                mode="multiple"
                                filterOption={(input, option) => {
                                    return (
                                        option.props.children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    )
                                }}
                                defaultValue={(props.pageAnalysisConfig.associated_domains || []).map((ad) => ad.id)}
                                onChange={(e: number[]) => {
                                    form.setFieldsValue({
                                        associated_domains: e.map((did) => {
                                            const domain = domains.find((d) => d.id === did)
                                            return {
                                                id: domain.id,
                                                name: domain.name,
                                            }
                                        }),
                                    })
                                }}
                            >
                                {domains?.map((d) => {
                                    return (
                                        <Select.Option key={d.id} value={d.id}>
                                            {d.display_name}
                                        </Select.Option>
                                    )
                                })}
                            </Select>
                        </Form.Item>
                    </Form>
                )}
            </Drawer>
        )
    }

    return render()
}

export default EditPageAnalysisConfigDrawer
